import { ref, type Ref } from 'vue'
import { useToast } from '~/components/ui/toast'
import type { ITtsServiceResponse, TtsLanguage } from '~/types'

interface ResolveAudioUrlParams {
  text: string
  slug: string
  language: TtsLanguage
}

export interface UseTtsServiceReturn {
  isGenerating: Ref<boolean>
  resolveAudioUrl: (params: ResolveAudioUrlParams) => Promise<string | null>
}

/**
 * Resolves a news article to a playable audio URL.
 *
 * Goes through our own /api/tts/* routes, so the service address never appears
 * in the browser. Returns only once the file exists, so the URL is ready to play.
 */
export function useTtsService(): UseTtsServiceReturn {
  const { toast } = useToast()
  const { t } = useI18n()

  const isGenerating = ref(false)

  const resolveAudioUrl = async ({
    text,
    slug,
    language
  }: ResolveAudioUrlParams): Promise<string | null> => {
    if (isGenerating.value) return null

    isGenerating.value = true

    try {
      const response = await $fetch<ITtsServiceResponse>('/api/tts/generate', {
        method: 'POST',
        body: { text, slug, language }
      })

      return response?.url || null
    } catch (error) {
      toast({
        title: t('error.title'),
        description: t('tts.generation_error'),
        variant: 'destructive'
      })
      console.error('TTS error:', error)
      return null
    } finally {
      isGenerating.value = false
    }
  }

  return { isGenerating, resolveAudioUrl }
}
